import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrders, updateOrderStatus, fetchOrderDetails } from '../store/slices/orderSlice';
import { RootState, AppDispatch } from '../store';
import { EyeIcon, PencilIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { getImageUrl } from '../constants/apiConstants';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';
import { orderAPI } from '../services/orderAPI';

const Orders: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { orders = [], selectedOrder, totalOrders = 0, currentPage = 1, totalPages = 0, isLoading } = useSelector((state: RootState) => state.orders);
  
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [newStatus, setNewStatus] = useState('');

  const orderStatuses = ['PLACED', 'CONFIRMED', 'SHIPPED', 'DELIVERED', 'CANCELLED'];

  useEffect(() => {
    console.log('Fetching orders with params:', { page, status: statusFilter, limit: 10 });
    dispatch(fetchOrders({ page, status: statusFilter, limit: 10 }));
  }, [dispatch, page, statusFilter]);

  // Debug pagination values
  useEffect(() => {
    console.log('Pagination values:', { currentPage, totalPages, totalOrders });
  }, [currentPage, totalPages, totalOrders]);

  const handleDownloadReceipt = async (orderId: string) => {
    try {
      const response = await fetch(`https://rudraksh-api.volvrit.org/api/rudraksh/order/get-details/${orderId}?receipt=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `order-${orderId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Receipt downloaded successfully');
      } else {
        toast.error('Failed to download receipt');
      }
    } catch (error) {
      toast.error('Error downloading receipt');
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await orderAPI.downloadCSV({ status: statusFilter });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('CSV downloaded successfully');
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const handleViewOrder = async (orderId: string) => {
    try {
      await dispatch(fetchOrderDetails(orderId)).unwrap();
      setShowOrderModal(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch order details');
    }
  };

  const handleCloseOrderModal = () => {
    setShowOrderModal(false);
  };

  const getOrderSteps = (status: string) => {
    const steps = [
      { key: 'PLACED', label: 'Order Placed', icon: '📝' },
      { key: 'CONFIRMED', label: 'Confirmed', icon: '✅' },
      { key: 'SHIPPED', label: 'Shipped', icon: '🚚' },
      { key: 'DELIVERED', label: 'Delivered', icon: '📦' }
    ];
    
    const currentIndex = steps.findIndex(step => step.key === status);
    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      active: index === currentIndex
    }));
  };

  const handleStatusUpdate = async () => {
    if (!editingOrder || !newStatus) return;
    
    try {
      await dispatch(updateOrderStatus({ id: editingOrder._id, status: newStatus })).unwrap();
      toast.success('Order status updated successfully');
      setShowStatusModal(false);
      dispatch(fetchOrders({ page, status: statusFilter, limit: 10 }));
    } catch (error: any) {
      toast.error(error.message || 'Failed to update order status');
    }
  };

  const openStatusModal = (order: any) => {
    setEditingOrder(order);
    setNewStatus(order.orderStatus);
    setShowStatusModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PLACED': return 'bg-blue-100 text-blue-800';
      case 'CONFIRMED': return 'bg-yellow-100 text-yellow-800';
      case 'SHIPPED': return 'bg-purple-100 text-purple-800';
      case 'DELIVERED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Orders Management</h1>
        <p className="mt-2 text-sm text-gray-700">Manage all customer orders</p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Filter by Status</label>
            <select
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Orders</option>
              {orderStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleDownloadCSV}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Download CSV
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Orders ({totalOrders})
          </h3>
        </div>
        
        {isLoading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(orders) && orders.filter(Boolean).map((order: any) => (
                  <tr key={order._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        #{order?._id?.slice(-8)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {order?.userId?.fullName || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {order?.userId?.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {order?.items?.length || 0} item(s)
                      </div>
                      <div className="text-sm text-gray-500">
                        {order?.items?.[0]?.product?.productname || 'Product'}
                        {order?.items?.length > 1 && ` +${order.items.length - 1} more`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ₹{order?.payableAmount || 0}
                      </div>
                      <div className="text-sm text-gray-500">
                        {order?.paymentMethod || 'COD'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        getStatusColor(order?.orderStatus || 'PLACED')
                      }`}>
                        {order?.orderStatus || 'PLACED'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order?.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleViewOrder(order._id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDownloadReceipt(order._id)}
                          className="text-green-600 hover:text-green-900"
                          title="Download Receipt"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openStatusModal(order)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Update Status"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(newPage) => setPage(newPage)}
          />
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowStatusModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Update Order Status
                  </h3>
                  <button onClick={() => setShowStatusModal(false)}>
                    <XMarkIcon className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Order ID: #{editingOrder?._id?.slice(-8)}
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    Customer: {editingOrder?.userId?.fullName}
                  </p>
                  
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order Status
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    {orderStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleStatusUpdate}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Update Status
                </button>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseOrderModal}></div>
            
            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all max-w-4xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Order Details</h3>
                  <button type="button" onClick={handleCloseOrderModal}>
                    <XMarkIcon className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
                
                {/* Order Stepper */}
                <div className="mb-8">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Order Progress</h4>
                  <div className="flex items-center justify-between">
                    {getOrderSteps(selectedOrder.orderStatus).map((step, index) => (
                      <div key={step.key} className="flex flex-col items-center flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                          step.completed ? 'bg-green-500 text-white' : 
                          step.active ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {step.icon}
                        </div>
                        <div className="mt-2 text-xs text-center">
                          <div className={`font-medium ${
                            step.completed || step.active ? 'text-gray-900' : 'text-gray-500'
                          }`}>
                            {step.label}
                          </div>
                        </div>
                        {index < getOrderSteps(selectedOrder.orderStatus).length - 1 && (
                          <div className={`absolute h-0.5 w-full top-5 left-1/2 transform -translate-y-1/2 ${
                            step.completed ? 'bg-green-500' : 'bg-gray-200'
                          }`} style={{ zIndex: -1 }} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Order Info */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-3">Order Information</h4>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Order ID:</span>
                          <span className="ml-2 text-sm text-gray-900">#{selectedOrder._id?.slice(-8)}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Status:</span>
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            getStatusColor(selectedOrder.orderStatus)
                          }`}>
                            {selectedOrder.orderStatus}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Payment Method:</span>
                          <span className="ml-2 text-sm text-gray-900">{selectedOrder.paymentMethod || 'COD'}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Total Amount:</span>
                          <span className="ml-2 text-sm font-medium text-gray-900">₹{selectedOrder.payableAmount}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Order Date:</span>
                          <span className="ml-2 text-sm text-gray-900">{new Date(selectedOrder.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Customer Info */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-3">Customer Information</h4>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm font-medium text-gray-500">Name:</span>
                          <span className="ml-2 text-sm text-gray-900">{selectedOrder.userId?.fullName || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Email:</span>
                          <span className="ml-2 text-sm text-gray-900">{selectedOrder.userId?.email || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">Phone:</span>
                          <span className="ml-2 text-sm text-gray-900">{selectedOrder.userId?.phoneNumber || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Shipping Address */}
                    {selectedOrder.shippingAddress && (
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">Shipping Address</h4>
                        <div className="text-sm text-gray-900">
                          <div>{selectedOrder.shippingAddress.street}</div>
                          <div>{selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state}</div>
                          <div>{selectedOrder.shippingAddress.zipCode}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Order Items */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Order Items</h4>
                    <div className="space-y-3">
                      {selectedOrder.items?.map((item: any, index: number) => {
                        console.log('Order item:', item); // Debug log
                        return (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex-shrink-0">
                            {item.product?.images?.[0] ? (
                              <img
                                className="h-12 w-12 rounded object-cover"
                                src={getImageUrl(item.product.images[0])}
                                alt={item.product?.productname}
                                onError={(e) => {
                                  console.log('Image failed to load:', getImageUrl(item.product?.images?.[0]));
                                  console.log('Product data:', item.product);
                                }}
                              />
                            ) : (
                              <div className="h-12 w-12 rounded bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                                No Image
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {item.product?.productname || 'Product'}
                            </div>
                            <div className="text-sm text-gray-500">
                              Qty: {item.quantity} × ₹{item.price}
                            </div>
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            ₹{item.quantity * item.price}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                    
                    {/* Order Summary */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Subtotal:</span>
                          <span className="text-gray-900">₹{selectedOrder.totalAmount || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Shipping:</span>
                          <span className="text-gray-900">₹{selectedOrder.shippingCost || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm font-medium border-t pt-2">
                          <span className="text-gray-900">Total:</span>
                          <span className="text-gray-900">₹{selectedOrder.payableAmount}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => handleDownloadReceipt(selectedOrder._id)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Download Receipt
                </button>
                <button
                  type="button"
                  onClick={handleCloseOrderModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {Array.isArray(orders) && orders.length === 0 && !isLoading && (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No orders found.
        </div>
      )}
    </div>
  );
};

export default Orders;